<script setup lang="ts">
/**
 * MobileBottomNav – horizontally-scrollable bottom tab bar for mobile screens.
 *
 * Positioned absolutely at the bottom of the nearest positioned ancestor.
 * A "Hide menu" handle slides the bar off-screen; a floating restore button
 * brings it back — the mobile equivalent of the desktop sidebar collapse toggle.
 *
 * Accepts the same TabItem props as NavigationButtonPanel so both can be
 * driven by the same tabs array from the parent.
 */
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { TabItem } from '@/components/sidebar/NavigationButtonPanel.vue'
import { useLogout } from '@/composables/useLogout'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import LocaleSwitcher from '@/components/sidebar/LocaleSwitcher.vue'

defineProps<{
  /** Tabs to display; each drives a route and optionally exposes a quick-add action. */
  tabs: TabItem[]
}>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const hidden = ref(false)
const { showLogoutConfirm, loggingOut, requestLogout, confirmLogout, cancelLogout } = useLogout()

function isActive(path: string): boolean {
  return route.path === path
}

function goTo(path: string) {
  if (route.path !== path) {
    router.push(path)
  }
}
</script>

<template>
  <div class="tabbar-wrap">
    <nav class="tabbar" :class="{ 'tabbar--hidden': hidden }" :aria-label="t('nav.tabNavigation')">
      <div
        class="tabbar-handle"
        role="button"
        tabindex="0"
        :aria-label="t('nav.hideMenu')"
        @click="hidden = true"
        @keydown.enter.space.prevent="hidden = true"
      >
        <span>{{ t('nav.hideMenu') }}</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      <div class="tabs" role="tablist">
        <button
          v-for="(tab, index) in tabs"
          :key="tab.path"
          type="button"
          class="tab"
          :class="{ 'tab--active': isActive(tab.path) }"
          role="tab"
          :aria-selected="isActive(tab.path)"
          :id="`mobile-tab-${index}`"
          @click="goTo(tab.path)"
        >
          {{ tab.label }}
          <span
            v-if="tab.onAdd"
            class="tab-add"
            :aria-label="t('nav.addNew')"
            @click.stop="tab.onAdd!()"
            >+</span
          >
        </button>

        <button type="button" class="tab tab--logout" :disabled="loggingOut" @click="requestLogout">
          {{ t('nav.logOut') }}
        </button>

        <LocaleSwitcher class="tab-locale-switcher" />
      </div>
    </nav>

    <button
      class="restore"
      :class="{ 'restore--show': hidden }"
      type="button"
      :aria-label="t('nav.showMenu')"
      :tabindex="hidden ? 0 : -1"
      @click="hidden = false"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M4 7h16M4 12h16M4 17h16" />
      </svg>
    </button>
  </div>

  <DeleteConfirmSheet
    :open="showLogoutConfirm"
    :title="t('logout.confirmTitle')"
    :description="t('logout.confirmDescription')"
    :confirm-label="t('logout.confirmLabel')"
    :pending="loggingOut"
    @confirm="confirmLogout"
    @cancel="cancelLogout"
  />
</template>

<style scoped>
.tabbar-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  pointer-events: none;
}

.tabbar {
  pointer-events: auto;
  background: color-mix(in srgb, var(--color-tufte-bg) 88%, transparent);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-top: 1px solid var(--color-tufte-border);
  transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
  padding-bottom: 22px; /* home indicator clearance */
}

.tabbar--hidden {
  transform: translateY(100%);
}

.tabbar-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid color-mix(in srgb, var(--color-tufte-border) 60%, transparent);
  font-family: var(--font-tufte-sans);
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  cursor: pointer;
  user-select: none;
}

.tabbar-handle svg {
  width: 0.85rem;
  height: 0.85rem;
}

.tabs {
  display: flex;
  gap: 0.4rem;
  overflow-x: auto;
  padding: 0.6rem 16px 0.5rem;
  scrollbar-width: none;
  scroll-snap-type: x proximity;
}

.tabs::-webkit-scrollbar {
  display: none;
}

.tab {
  position: relative;
  flex: 0 0 auto;
  scroll-snap-align: start;
  font-family: var(--font-tufte);
  font-size: 1.02rem;
  color: var(--color-tufte-muted);
  background: none;
  border: 1px solid transparent;
  border-radius: 9px;
  padding: 0.5rem 0.85rem;
  line-height: 1.05;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.12s,
    color 0.12s,
    border-color 0.12s;
}

.tab:active {
  background: color-mix(in srgb, var(--color-tufte-border) 30%, transparent);
}

.tab--active {
  color: var(--color-tufte-text);
  font-weight: 700;
  background: color-mix(in srgb, var(--color-tufte-border) 55%, var(--color-tufte-bg));
  border-color: var(--color-tufte-border);
}

.tab-add {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 1.05rem;
  height: 1.05rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-tufte-sans);
  font-size: 0.9rem;
  line-height: 1;
  color: var(--color-tufte-muted);
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  border-radius: 999px;
  pointer-events: auto;
  cursor: pointer;
}

.tab--active .tab-add {
  color: var(--color-tufte-text);
}

.tab--logout {
  color: var(--color-tufte-danger);
  border-left: 1px solid var(--color-tufte-border);
  border-radius: 0;
  margin-left: 0.2rem;
  padding-left: 1rem;
}

.tab--logout:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 10%, transparent);
}

.tab--logout:disabled {
  opacity: 0.5;
  cursor: wait;
}

.tab-locale-switcher {
  flex: 0 0 auto;
  align-self: center;
  margin-left: 0.4rem;
}

.restore {
  pointer-events: auto;
  position: absolute;
  right: 14px;
  bottom: 26px;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999px;
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  box-shadow: 0 3px 10px rgb(0 0 0 / 12%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-tufte-text);
  cursor: pointer;
  opacity: 0;
  transform: scale(0.8) translateY(8px);
  transition:
    opacity 0.2s,
    transform 0.2s;
}

.restore--show {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.restore svg {
  width: 1.2rem;
  height: 1.2rem;
}
</style>
